import DateTimePicker from 'react-datetime-picker';
import { Formik, FormikHelpers } from 'formik';
import toast from 'react-hot-toast';
import React from 'react';

import { useDownloadFile } from '@project/hooks/global';
import { ReportFilterProps } from '@project/types';
import { userService } from '@project/services';
import { Button } from '@project/components';

import { FormikFormSchema } from '../schemas/schema';

export const ArchivedAccountsReportForm: React.FC = () => {
  const initialValues: ReportFilterProps = {
    fromDate: null,
    toDate: null,
    filterDateBy: 'CreatedDate',
  };

  const { download, loading } = useDownloadFile({
    apiDefinition: async (params: ReportFilterProps) =>
      userService.getArchivedAccountsReport(params),
    onSuccess: () => toast.success('File downloaded successfully!'),
    onError: () => toast.error('Could not download report'),
  });

  const handleSubmission = async (
    values: ReportFilterProps,
    { setSubmitting }: FormikHelpers<ReportFilterProps>,
  ) => {
    try {
      await download(values);
    } catch (error) {
      toast.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validateOnBlur
      validateOnChange={false}
      onSubmit={handleSubmission}
      validationSchema={FormikFormSchema}
    >
      {({ handleSubmit, isSubmitting, values, handleReset, setFieldValue, errors, touched }) => {
        const _setFromDate = (e) => {
          if (e) {
            const d = new Date(e);
            d.setHours(2, 0, 0, 0);
            setFieldValue('fromDate', d);
          } else {
            setFieldValue('fromDate', e);
          }
        };
        const _setToDate = (e) => {
          if (e) {
            const d = new Date(e);
            d.setHours(23, 59, 0, 0);
            setFieldValue('toDate', d);
          } else {
            setFieldValue('toDate', e);
          }
        };
        return (
          <form
            onReset={handleReset}
            onSubmit={handleSubmit}
            className="flex bg-gray-100 shadow-lg rounded-sm"
          >
            <div className="flex flex-col justify-start items-start p-4 gap-4 w-1/2">
              <span className="w-full text-xl mb-2">Download Archived Accounts Report</span>
              <label htmlFor="filter-date-from" className="w-4/5 flex flex-col ">
                Filter From
                <DateTimePicker
                  id="filter-date-from"
                  clearIcon={null}
                  format="y-MM-dd"
                  onChange={_setFromDate}
                  value={values.fromDate}
                  maxDate={values.toDate ? new Date(values.toDate) : new Date()}
                  disableClock
                />
                {touched.fromDate && errors.fromDate && (
                  <span className="text-red-500 text-sm mt-1">{errors.fromDate}</span>
                )}
              </label>
              <label htmlFor="filter-date-to" className="w-4/5 flex flex-col">
                Filter To
                <DateTimePicker
                  id="filter-date-to"
                  clearIcon={null}
                  format="y-MM-dd"
                  onChange={_setToDate}
                  value={values.toDate}
                  minDate={values.fromDate ? new Date(values.fromDate) : undefined}
                  maxDate={new Date()}
                  disableClock
                />
                {touched.toDate && errors.toDate && (
                  <span className="text-red-500 text-sm mt-1">{errors.toDate}</span>
                )}
              </label>
              <Button
                isLoading={isSubmitting || loading}
                onClick={handleSubmit}
                className="bg-tertiary hover:bg-tertiary-dark text-white text-base w-auto"
              >
                Download
              </Button>
            </div>
            <div className="w-1/2 bg-gray-200 rounded-sm m-5 p-2">
              <h1>Description</h1>
              <p className="pl-3 text-sm">
                The report lists archived accounts along with associated details: <br /> Email,
                Phone Number, Full Name.
              </p>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};
